export const DEBUG_STORES_INSTANCE = false

import {socketIO} from '@stores/socketIO'
import { assign, pick, trimEnd } from 'lodash'
import {TabNode, TabStore} from '@flexlayout'
import {contextMenuStore, ContextMenuStore, DetailsRouteDashboardStore, FilterChipsToolbarStore, FilterStore, SidebarStore} from '@modules'
import {CloudPDFStore, IFlexLayoutPdfTabConfig, IQueuedPdfServerJob, PdfExportStore} from '@modules/pdf'
import {C8Store} from '@stores/c8'
import {CatalogPresetsStore, CatalogStore, GroupByStore, FlexLayoutStore, LibraryItemConfigWrapper, IFlexLayoutCatalogRootConfig} from '@stores/catalog'
import {CatalogRootFlexLayoutStore} from '@stores/catalog/flexLayout'
import {VipFlexLayoutTabStore} from '@stores/catalog/flexLayout/flexComponents/VipFlexLayoutTab'
import {CommentsStore} from '@stores/comments'
import {configs, ICatalogFieldConfig} from '@stores/config'
import {DragDropStore} from '@stores/dragDrop'
import {ElasticSearchStore} from '@stores/elasticsearch'
import {ExportStore} from '@stores/export'
import {appStore, globalStores, jira, ReportsStore, Stores} from '@stores/index'
import {UserNotificationsStore} from '@stores/notifications'
import {SyncProgressStore} from '@stores/sync'
import {TagsStore} from '@stores/tags'
import {CurrentUserStore, UsersStore} from '@stores/user'
import {VLPStore} from '@stores/vlp'
import {Workspace, WorkspacesStore} from '@stores/workspace'
import {action, autorun, computed, observable, runInAction} from 'mobx'
// import {nest} from './nestjs'
import {dev} from '@modules/dev/DevStore'
import utils from '../modules/common/utils'
import {deployments} from '@stores/dev'
import {React, thirdparty} from '../shared/components'
import {site} from './site'
import {apolloStore as apollo} from './apollo'
import {nav} from './nav'
import {strings, StringsStore} from './StringsStore'
import {auth, log, error, authStore, guid} from '@utils'

export {FilterChipsToolbarStore} from '@modules/catalog/FilterChipsToolbar/store'
import {mobxDevTool} from './dev'
import {ui, UIStore} from './ui'
import {aws, AWSStore} from './aws'

export class StoresInstance implements Partial<Stores> {
  get allStores() { return StoresInstance.allStores}

  @action dispose = () => {
    DEBUG_STORES_INSTANCE && console.log(`Dispose store ${this.storeId}`)
    this.filter?.dispose()
    this.catalog?.dispose()
    //this.dragDrop?.dispose()
    this.vlp?.dispose()
    this.sidebar?.dispose()
    this.workspaces?.dispose()
    this.tags?.dispose()
    this.cloudPDF?.dispose()

    this.disposed = true
    if (window['stores']) {
      delete window['stores'][this._id]
    }
    StoresInstance.allStores.delete(this._id)
  }
  @observable disposed = false

  @observable.shallow vipTabs: { [componentName: string]: VipFlexLayoutTabStore } = {}
  @observable.shallow pageStores: {
    detailsRoute?: DetailsRouteDashboardStore
  } = {}
  get pdfConfig() { return this.extra?.tab?.component === 'pdfViewer' ? this.extra.tab?.config as IFlexLayoutPdfTabConfig : null}
  get queryClient() { return site.queryClient}

  get catalogTab() { return this.vipTabs.catalog}
  get tab() { return this.extra?.tab ?? this.catalogTab?.tab}

  @computed get isLibraryItemsList() { return this.purpose === 'library-items-list'}

  @computed get isInlineDetailsStore() { return this.purpose === 'inline-details'}

  @computed get isFilterLibrariesStore() { return this.purpose === 'library-filters'}

  @computed get isCurrentWorkspace() { return !this.workspace || this.catalog?.id == globalStores.workspaces.current}

  @computed get isGlobalStore() {
    return this.storeId === 'global'
  }

  app = appStore
  flexLayoutCatalogRoot: CatalogRootFlexLayoutStore
  auth = auth
  log = log
  error = error
  config = configs
  authStore = authStore
  apollo = apollo
  deployments = deployments
  dev = dev
  jira = jira
  socketIO = socketIO

  get deployment() { return this.deployments.deploymentConfig}
  @observable.ref sidebar: SidebarStore

  @observable.ref nav = nav
  @observable.ref site = site
  strings = strings
  ui = ui
  //dev: DEBUG && new DevStore()
  users: UsersStore
  currentUser: CurrentUserStore

  get userSettings() { return this.currentUser?.userSettings}
  thirdparty = thirdparty
  utils = utils
  userNotifications: UserNotificationsStore
  mobxDevTool = mobxDevTool
  contextMenu = contextMenuStore

  // nest = nest
  @observable.ref workspaces: WorkspacesStore
  @observable.ref pdf: PdfExportStore
  @observable.ref comments: CommentsStore
  @observable.ref cloudPDF: CloudPDFStore

  // get workspaceStores() : Partial<WorkspaceStores> {
  //   return null //return useWorkspaceStores()
  // }
  //
  // get stores() : Partial<Stores> { return this.workspaceStores?.stores}
  //
  get groupBy(): GroupByStore { return this.catalog.groupBy}

  // get catalog() : Partial<CatalogStore> {
  //   return this.stores?.catalog ?? {}
  // }
  //
  // get dragDrop() : Partial<DragDropStore> {
  //   return this.stores?.dragDrop ?? {}
  // }
  //
  // get vlp() : Partial<VLPStore> { return this.stores?.vlp ?? {}}
  //
  // get upload() : Partial<UploadStore> { return this.stores?.upload ?? {}}
  //
  // get contextMenu() : Partial<ContextMenuStore> { return this.stores?.contextMenu ?? {}}
  //
  // get filterChipsToolbar() : Partial<FilterChipsToolbarStore> { return this.stores?.filterChipsToolbar ?? {}}
  // get syncProgress() : Partial<SyncProgressStore> { return this.stores?.syncProgress ?? {}}

  @observable.ref catalog: CatalogStore
  @observable.ref filterChipsToolbar: FilterChipsToolbarStore
  @observable.ref dragDrop: DragDropStore
  @observable.ref export: ExportStore
  @observable.ref syncProgress = new SyncProgressStore(this)
  @observable.ref presets: CatalogPresetsStore
  @observable.ref tags: TagsStore
  @observable.ref vlp: VLPStore

  @observable.ref filter: FilterStore
  @observable.ref c8: C8Store
  @observable.ref es: ElasticSearchStore
  @observable.ref flexLayout: FlexLayoutStore

  @observable.ref aws = aws

  @observable.ref reports: ReportsStore

  @computed get isActiveCatalogRoot() {
    const {flexLayout: {activeOrLastCatalogRoot}} = this
    return activeOrLastCatalogRoot?.stores == this || activeOrLastCatalogRoot?.storesToInject == this
  }

  @computed get isActiveTab() {
    return this.flexLayout?.activeTab?.storesToInject === this || this.flexLayout.activeOrLastCatalogRoot?.storesToInject === this
  }

  get storeId() {
    const {workspace, _id, description} = this
    return `${_id ?? (workspace ? workspace?.id : 'unknown')}`
  }

  static allStores = observable.map<string, StoresInstance>({}, {deep: false})

  @observable.ref _workspace: Workspace
  @computed get workspace() {
    return this._workspace //?? this.workspaces?.workspace
  }

  /**
   * Stores get reused when the user switches to a draft or reverts changes so we don't have to reload everything from scratch
   */
  @observable reused = false

  @computed get nestJSUrl() {
    const {userSettings: { dev}, config: {config: {secondaryServicesUrl}}} = this

    return DEBUG && dev?.localNest ? 'http://localhost:5959' : trimEnd(secondaryServicesUrl, '/')
  }

  constructor(
    public description: string,
    workspace?: Workspace,
    public _id?: string,
    public purpose ?: string | 'change-log' | 'library-items-list' | 'inline-details' | 'library-filters' | 'whiteboard' | 'pdf',
    public extra?: {
      tab?: TabNode,
      assignableField?: ICatalogFieldConfig,
      library?: LibraryItemConfigWrapper
      parent?: StoresInstance
    },
    existingStoresToCopyFrom?: StoresInstance
  ) {
    const {storeId} = this, {allStores} = StoresInstance, parentStores = extra?.parent

    if (_id === 'global') {
      autorun(() => {
        if (!this.authStore.userIsAuthenticated && allStores) {
          const vals = Array.from(allStores.values()).filter(s => !s.isGlobalStore)
          runInAction(() => {
            vals.forEach(s => {
              allStores.delete(s._id)
              s.dispose()
            })
          })
        }
      })
    }

    this._workspace = workspace

    if (allStores.has(storeId)) {
      const existing = allStores.get(storeId)
      if (existing.workspace === workspace && existing.purpose === purpose) {
        DEBUG_STORES_INSTANCE && !TEST && console.warn(`There is already a store instance with the id ${storeId} - using existing...`)
        existing.reused = true
        if (existing.filter) {
          existing.filter.reset()
        }

        if (existing.tab?.id === 'catalogItemDetailsTab') {
          const {sidebar: {left, right}} = existing
          left.isDisposed && left.registerEffects()
          right.isDisposed && right.registerEffects()
        }
        return existing
      } else {
        existing.dispose()
      }
    }

    DEBUG_STORES_INSTANCE && !TEST && console.groupCollapsed(`Creating store instance '${storeId}'`, {storeId, purpose, description, workspace: workspace?._id})
    // Causes huge memory bloat
    //_.merge(window['stores'], {[storeId]: this})
    allStores.set(storeId, this)

    // if (sourceCatalog) {
    //   this.catalog = sourceCatalog
    //   this.flexLayout = sourceCatalog.flexLayout
    //   this.users = sourceCatalog.stores.users
    //   this.currentUser = sourceCatalog.stores.currentUser
    //   this.contextMenu = sourceCatalog.stores.contextMenu
    //
    //   this.userNotifications = sourceCatalog.stores.userNotifications
    //   this.vlp = sourceCatalog.stores.vlp
    //   this.c8 = sourceCatalog.stores.c8
    //
    //
    //   // Intentionally a new instance, we're doing this for inline details where we need to reuse the catalog but have different sidebar detail items
    //   this.sidebar = new SidebarStore(sourceCatalog)
    //
    //   this.filter = sourceCatalog.stores.filter
    //   this.dragDrop = sourceCatalog.stores.dragDrop
    //   this.filterChipsToolbar = sourceCatalog.stores.filterChipsToolbar
    //   this.workspaces = sourceCatalog.stores.workspaces
    //
    //   this.pdf = sourceCatalog.stores.pdf
    //   this.export = sourceCatalog.stores.export
    //   this.es = sourceCatalog.stores.es
    //   this.tags = sourceCatalog.stores.tags
    //   this.presets = sourceCatalog.stores.presets
    // }
    // else {
    if (existingStoresToCopyFrom) {
      assign(this, {...existingStoresToCopyFrom})
    } else {
      this.catalog = new CatalogStore(null, this)
      const {catalog, isGlobalStore} = this

      /// region Global store Instances

      /// endregion

      if (isGlobalStore) {
        this.users = new UsersStore(catalog)
        this.flexLayout = new FlexLayoutStore(catalog)
        this.currentUser = new CurrentUserStore(catalog)
        this.userNotifications = new UserNotificationsStore(catalog)
        this.reports = new ReportsStore(catalog)
        this.comments = new CommentsStore(catalog)
      }
      else {
        this.vlp = new VLPStore(catalog)
        this.pdf = new PdfExportStore(this)

        Object.assign(this, pick(globalStores, ['cloudfront', 'users', 'comments', 'flexLayout', 'currentUser', 'userNotifications', 'reports']))

        if (!parentStores) {
          this.vlp = new VLPStore(catalog)
          this.pdf = new PdfExportStore(this)
        }
      }

      this.sidebar = new SidebarStore(catalog)
      this.filter = new FilterStore(catalog, extra?.library)
      this.dragDrop = new DragDropStore(catalog)

      if (parentStores) {
        assign(this, pick(parentStores, ['cloudPDF', 'strings', 'filterChipsToolbar', 'workspaces', 'c8', 'export', 'es', 'tags', 'presets', 'vlp', 'pdf']))
      }
      else {
        this.cloudPDF = new CloudPDFStore(catalog)
        this.filterChipsToolbar = new FilterChipsToolbarStore(catalog)
        this.workspaces = new WorkspacesStore(catalog)
        this.c8 = new C8Store(catalog)
        this.export = new ExportStore(catalog)
        this.es = new ElasticSearchStore(catalog)
        this.tags = new TagsStore(catalog)
        this.presets = new CatalogPresetsStore(catalog)
      }
    }
    // }

    DEBUG_STORES_INSTANCE && !TEST && console.groupEnd()
  }

  @computed get initialized() {
    const {catalog, filter, purpose} = this
    return !purpose || (catalog.initialized && filter.initialized)
  }

  @computed static get anyStoresNotInitialized() {
    const {allStores} = StoresInstance
    return Array.from(allStores.values()).some(s => !s.isGlobalStore && !s.purpose && !s.initialized)
  }

  static disposeAllButGlobal = () => {
    Array.from(StoresInstance.allStores.values()).filter(s => !s.isGlobalStore).map(s => s.dispose())
  }
}



// WEBPACK FOOTER //
// ./src/stores/StoresInstance.ts